import React from "react";
import { ApiPaths } from "jadro-pico/api-endpoints/ApiPaths";
import type { ApiResponse } from "jadro-pico/dtos/common/ApiResponse";
import type { OperationHistoryDto } from "jadro-pico/dtos/pico_com/OperationHistoryDto";
import type { PicoAppEnum } from "jadro-pico/apps/PicoAppEnum";
import PageLayout from "../Layouts/PageLayout/PageLayout";
import BackHeaderPage from "../Layouts/PageLayout/BackHeaderPage";
import SectionCard from "../SectionCard/SectionCard";
import { SectionCardVariantEnum } from "../SectionCard/SectionCardVariantEnum";
import List from "../List/List";
import EmptyState from "../EmptyState/EmptyState";
import ErrorState from "../ErrorState/ErrorState";
import Skeleton from "../Skeleton/Skeleton";
import { InfiniteScrollSentinel } from "../DataTable/InfiniteScrollSentinel";
import { ThemeColorEnum, ThemeIconEnum } from "../Themes/ThemeEnums";
import { useInfiniteApiQuery } from "../../../Api/useInfiniteApiQuery";
import { useResource } from "../../../Resources/useResource";
import { RequestQueueResources } from "../../../Resources/RequestQueueResources";
import { historyRowToQueueItem } from "../../../Offline/requestQueueItems";
import { OPERATION_HISTORY_QUERY_KEY } from "../../../Api/queryKeys";
import { useRequestQueueRow } from "./RequestQueueRow";
import { useQueueBack } from "./useQueueBack";
import { useRoutePaths } from "../../../Routes/useRoutePaths";
import { RequestQueueRoutes } from "../../../Routes/RequestQueueRoutes";

const PAGE_SIZE = 50;

interface RequestQueueHistoryProps {
  apiUrl: string;
  app: PicoAppEnum;
}

const RequestQueueHistory: React.FC<RequestQueueHistoryProps> = ({ apiUrl, app }) => {
  const resources = useResource(RequestQueueResources);
  const renderRow = useRequestQueueRow();
  const goBack = useQueueBack(useRoutePaths(RequestQueueRoutes).REQUEST_QUEUE_MINE);

  const { items, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError, refetch } =
    useInfiniteApiQuery<OperationHistoryDto, string>({
      queryKey: [...OPERATION_HISTORY_QUERY_KEY, app, "all"],
      pageSize: PAGE_SIZE,
      buildUrl: (cursor, limit) =>
        `${apiUrl}${ApiPaths.operations.prefix}?app=${app}&limit=${limit}` +
        (cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""),
      selectPage: (raw) => {
        const rows = (raw as ApiResponse<OperationHistoryDto[]>).data ?? [];
        return {
          items: rows,
          nextCursor:
            rows.length < PAGE_SIZE ? null : (rows[rows.length - 1]?.operationUuid ?? null),
        };
      },
    });

  const body = (): React.ReactElement => {
    if (isLoading && items.length === 0) return <Skeleton height={200} />;
    if (isError && items.length === 0)
      return (
        <ErrorState
          headline={resources.errorHeadline}
          retry={{ label: resources.retryLabel, onClick: () => void refetch() }}
        />
      );
    if (items.length === 0)
      return (
        <EmptyState
          icon={ThemeIconEnum.CHECK}
          headline={resources.historyEmptyHeadline}
          supporting={resources.historyEmptySupporting}
        />
      );
    return (
      <SectionCard variant={SectionCardVariantEnum.SURFACE} flush>
        <List dividers>{items.map((row) => renderRow(historyRowToQueueItem(row)))}</List>
        {hasNextPage && (
          <InfiniteScrollSentinel
            onEndReached={fetchNextPage}
            isFetchingMore={isFetchingNextPage}
          />
        )}
      </SectionCard>
    );
  };

  return (
    <PageLayout maxWidth={720} gap={16} background={ThemeColorEnum.SURFACE_VARIANT}>
      <BackHeaderPage
        title={resources.historyTitle}
        backLabel={resources.closeAriaLabel}
        onBack={goBack}
      >
        {body()}
      </BackHeaderPage>
    </PageLayout>
  );
};

export default RequestQueueHistory;
